import { useEffect, useState } from "react";
import { Delete, Droplet, Pause, Play, RefreshCw, RotateCcw, Thermometer } from "lucide-react";
import { toast } from "sonner";
import type { AjustesMarcador, ClimaMarcador, MarcadorCompleto } from "@/types";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Avatar } from "@/components/marcador/Avatar";
import { IconoAjustes, IconoAudio, IconoPantalla, IconoPelota } from "@/components/marcador/iconos";
import { Publicidad } from "@/components/marcador/Publicidad";
import "@/components/marcador/marcador.css";

// Marcador de pádel con el diseño del marcador de pista clásico. Es UN solo componente para las dos
// pantallas: la tablet de árbitro (modo "tablet", con los botones de puntuación, el menú de la pelota,
// la rueda de ajustes y el audio) y el marcador fijo de la pista (modo "tv": exactamente lo mismo, pero
// sin ningún control — nadie lo maneja, solo enseña el resultado).

export interface ControlesMarcador {
  onPunto: (position: 1 | 2 | 3 | 4, tipo: 0 | 1 | 2) => void;
  onIniciar: () => void;
  onRegresar: () => void;
  onPausar: () => void;
  onReiniciar: () => void;
  onResetear: () => void;
  onAjustes: (ajustes: Partial<AjustesMarcador>) => void;
  onSaltarDescanso: () => void;
}

interface Props {
  modo: "tablet" | "tv";
  pista: string;
  club: string;
  clima: ClimaMarcador | null;
  // null = esta pista no tiene ahora mismo una partida (pantalla de espera).
  marcador: MarcadorCompleto | null;
  // Marca de tiempo (Date.now()) de cuando llegó `marcador`: con ella los relojes siguen contando
  // entre una consulta y la siguiente sin depender de que el reloj del dispositivo esté en hora.
  recibidoEn: number;
  controles?: ControlesMarcador;
  textoEspera?: string;
}

const COLORES_ESTADO = {
  CALENTAMIENTO: "#fb422e",
  EN_JUEGO: "#4caf50",
  PAUSADO: "#ff9800",
  DESCANSO: "#ff9800",
  FINALIZADO: "#c2c7c0",
  ESPERA: "#c2c7c0",
} as const;

function useAhora(): Date {
  const [ahora, setAhora] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setAhora(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return ahora;
}

const dos = (n: number) => String(n).padStart(2, "0");

function hhmmss(totalSegundos: number): string {
  const s = Math.max(0, Math.floor(totalSegundos));
  return `${dos(Math.floor(s / 3600))}:${dos(Math.floor((s % 3600) / 60))}:${dos(s % 60)}`;
}

const capitalizar = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

function textoFecha(d: Date): string {
  const dia = capitalizar(d.toLocaleDateString("es-ES", { weekday: "long" }));
  const mes = capitalizar(d.toLocaleDateString("es-ES", { month: "long" }));
  return `${dia} ${d.getDate()} ${mes}`;
}

// En diferencia de 2 la ventaja viaja como 50; en el tie-break se pintan los puntos tal cual.
function textoPuntos(valor: number, tipo: MarcadorCompleto["marcador"]["tipoJuego"]): string {
  if (tipo === "tiebreak" || tipo === "supertiebreak") return String(valor);
  return valor === 50 ? "AD" : String(valor);
}

type PanelAbierto = null | "ajustes" | "audio" | "menu";

export function PadelMarcador({ modo, pista, club, clima, marcador: m, recibidoEn, controles, textoEspera }: Props) {
  const ahora = useAhora();
  const esTablet = modo === "tablet" && !!controles;
  const [panel, setPanel] = useState<PanelAbierto>(null);
  const [confirmar, setConfirmar] = useState<null | "reiniciar" | "resetear">(null);

  const transcurrido = Math.max(0, Math.floor((ahora.getTime() - recibidoEn) / 1000));
  const segundos = m ? m.transcurridoSegundos + (m.corriendo ? transcurrido : 0) : 0;
  const descanso = m?.descansoSegundos != null ? m.descansoSegundos - transcurrido : null;
  const enDescanso = descanso != null && descanso > 0;

  const estado: keyof typeof COLORES_ESTADO = !m ? "ESPERA" : enDescanso ? "DESCANSO" : m.estado;
  const textoEstado = {
    CALENTAMIENTO: "CALENTAMIENTO",
    EN_JUEGO: "EN JUEGO",
    PAUSADO: "PAUSA",
    DESCANSO: "DESCANSO",
    FINALIZADO: "FINALIZADO",
    ESPERA: textoEspera ?? "SIN PARTIDA",
  }[estado];

  const puedePuntuar = esTablet && m?.estado === "EN_JUEGO" && !enDescanso;
  const hayPuntos = !!m && m.jugadores.some((j) => j.winners + j.erroresForzados + j.erroresNoForzados > 0);
  const empezada = !!m && m.estado !== "CALENTAMIENTO";

  function cerrarYEjecutar(accion: () => void) {
    setPanel(null);
    accion();
  }

  function pantallaCompleta() {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void document.documentElement.requestFullscreen?.().catch(() => toast.error("Este dispositivo no permite pantalla completa"));
  }

  // Las acciones del menú de la pelota se ven siempre igual (como en el original); si una no toca en este
  // momento, se avisa con un mensaje en vez de apagar el botón.
  function alIniciar(c: ControlesMarcador) {
    if (!m) return;
    if (m.estado === "EN_JUEGO") return void toast.info("La partida ya está en juego");
    if (m.estado === "FINALIZADO") return void toast.info("La partida ya ha terminado");
    cerrarYEjecutar(c.onIniciar);
  }
  function alRegresar(c: ControlesMarcador) {
    if (!hayPuntos || m?.estado === "FINALIZADO") return void toast.info("No hay ningún punto que regresar");
    cerrarYEjecutar(c.onRegresar);
  }
  function alPausar(c: ControlesMarcador) {
    if (m?.estado === "CALENTAMIENTO") return void toast.info("La partida todavía no ha empezado");
    if (m?.estado === "PAUSADO") return void toast.info("La partida ya está en pausa");
    if (m?.estado !== "EN_JUEGO") return;
    cerrarYEjecutar(c.onPausar);
  }
  function alReiniciar() {
    if (!empezada && !hayPuntos) return void toast.info("La partida ya está al principio");
    setPanel(null);
    setConfirmar("reiniciar");
  }

  function alPulsarEstadistica(position: 1 | 2 | 3 | 4, tipo: 0 | 1 | 2) {
    if (!controles || !m) return;
    if (m.estado === "CALENTAMIENTO") return toast.info("Inicia la partida para poder puntuar");
    if (m.estado === "PAUSADO") return toast.info("La partida está en pausa");
    if (!puedePuntuar) return;
    controles.onPunto(position, tipo);
  }

  // Caja de juego (gradiente según el tipo de juego) y cajas de set (verde ganado / naranja perdido).
  const tipoJuego = m?.marcador.tipoJuego ?? "normal";
  const claseJuego = tipoJuego === "oro" ? "mc-caja--oro" : tipoJuego === "normal" ? "mc-caja--normal" : "mc-caja--tiebreak";

  function cajaSet(equipo: "a" | "b", n: 0 | 1 | 2) {
    const caja = m?.marcador.sets[n];
    const iniciada = !!caja && caja.estado !== "pendiente" && m?.estado !== "CALENTAMIENTO";
    const propios = caja ? (equipo === "a" ? caja.a : caja.b) : 0;
    let clase = "mc-caja--pendiente";
    if (caja?.estado === "ganado_a") clase = equipo === "a" ? "mc-caja--ganado" : "mc-caja--perdido";
    if (caja?.estado === "ganado_b") clase = equipo === "b" ? "mc-caja--ganado" : "mc-caja--perdido";
    return (
      <div key={`${equipo}${n}`} className={`mc-caja ${clase}`}>
        {iniciada ? propios : ""}
      </div>
    );
  }

  function celdaJugador(posicion: 1 | 2 | 3 | 4) {
    const j = m?.jugadores.find((x) => x.position === posicion);
    const botones = [
      { tipo: 0 as const, clase: "winner", etiqueta: "Winner", n: j?.winners ?? 0 },
      { tipo: 1 as const, clase: "forzado", etiqueta: "Forzado", n: j?.erroresForzados ?? 0 },
      { tipo: 2 as const, clase: "noforzado", etiqueta: "No forzado", n: j?.erroresNoForzados ?? 0 },
    ];
    return (
      <div className="mc-jugador" key={posicion}>
        <div className="mc-insignia">
          J{posicion}
          {m?.estado === "EN_JUEGO" && m.marcador.saque === posicion && <span className="mc-saque" title="Al saque" />}
        </div>
        <div className="mc-avatar-caja">
          <Avatar nombre={j?.nombre ?? ""} fotoUrl={j?.fotoUrl ?? null} />
        </div>
        <span />
        <div className="mc-stats">
          {botones.map((b) =>
            esTablet ? (
              <button
                key={b.tipo}
                type="button"
                className={`mc-stat mc-stat--${b.clase} mc-stat--activo`}
                onClick={() => alPulsarEstadistica(posicion, b.tipo)}
              >
                <span>{b.etiqueta}</span>
                <span>{b.n}</span>
              </button>
            ) : (
              <div key={b.tipo} className={`mc-stat mc-stat--${b.clase}`}>
                <span>{b.etiqueta}</span>
                <span>{b.n}</span>
              </div>
            )
          )}
        </div>
        <span />
      </div>
    );
  }

  function filaEquipo(equipo: "a" | "b") {
    const posiciones = equipo === "a" ? ([1, 2] as const) : ([3, 4] as const);
    const puntos = m ? (equipo === "a" ? m.marcador.puntosA : m.marcador.puntosB) : 0;
    return (
      <div className="mc-rejilla">
        <div className="mc-equipo">
          <div className="mc-equipo-etiqueta">
            <span>Equipo {equipo.toUpperCase()}</span>
          </div>
          {posiciones.map(celdaJugador)}
        </div>
        <div className={`mc-caja ${claseJuego}`}>{m && m.estado !== "CALENTAMIENTO" ? textoPuntos(puntos, tipoJuego) : "0"}</div>
        {cajaSet(equipo, 0)}
        {cajaSet(equipo, 1)}
        {cajaSet(equipo, 2)}
      </div>
    );
  }

  const ganador = m?.marcador.ganadorEquipo ?? null;
  const nombresGanadores = ganador && m ? m.jugadores.filter((j) => (ganador === "a" ? j.position <= 2 : j.position >= 3)).map((j) => j.nombre) : [];
  const ajustes = m?.ajustes;
  const reglasEditables = m?.estado === "CALENTAMIENTO";

  return (
    <div className="mc-root">
      {/* ------------------------------------------------------------ cabecera */}
      <div className="mc-header">
        <div className="mc-logo">
          <img className={`mc-logo-img ${esTablet ? "" : "mc-logo-img--tv"}`} src="/scoreboard/logo-puntopadel.png" alt="Puntopadel.com" draggable={false} />
          {esTablet && controles && (
            <div className="mc-iconos">
              <button type="button" className="mc-icono" aria-label="Ajustes de la partida" aria-pressed={panel === "ajustes"} onClick={() => setPanel(panel === "ajustes" ? null : "ajustes")}>
                <IconoAjustes />
              </button>
              <button type="button" className="mc-icono" aria-label="Audio de la pista" aria-pressed={panel === "audio"} onClick={() => setPanel(panel === "audio" ? null : "audio")}>
                <IconoAudio />
              </button>
              <button type="button" className="mc-icono" aria-label="Pantalla completa" onClick={pantallaCompleta}>
                <IconoPantalla />
              </button>
              <button type="button" className="mc-icono" aria-label="Menú de la partida" aria-pressed={panel === "menu"} onClick={() => setPanel(panel === "menu" ? null : "menu")}>
                <IconoPelota />
              </button>
            </div>
          )}

          {/* ------------------------------------------------ paneles de la tablet */}
          {esTablet && controles && panel === "menu" && (
            <div className="mc-panel mc-panel-menu" style={{ paddingTop: "1.2cqh" }}>
              <button type="button" className="mc-menu-btn" onClick={() => alIniciar(controles)}>
                <Play fill="currentColor" />
                {m?.estado === "PAUSADO" ? "Reanudar Partida" : "Iniciar Partida"}
              </button>
              <button type="button" className="mc-menu-btn" onClick={() => alRegresar(controles)}>
                <Delete />
                Regresar Punto
              </button>
              <button type="button" className="mc-menu-btn" onClick={() => alPausar(controles)}>
                <Pause fill="currentColor" />
                Pausar Partida
              </button>
              <button type="button" className="mc-menu-btn" onClick={() => alReiniciar()}>
                <RotateCcw />
                Reiniciar Partida
              </button>
              <button type="button" className="mc-menu-btn" onClick={() => { setPanel(null); setConfirmar("resetear"); }}>
                <RefreshCw />
                Resetear Partida
              </button>
            </div>
          )}

          {esTablet && controles && panel === "ajustes" && ajustes && (
            <div className="mc-panel mc-panel-opciones">
              <div className="mc-opcion-fila">
                {([["SETS_3", "3 Sets"], ["SETS_2", "2 Sets"], ["JUEGOS_9", "9 Juegos"]] as const).map(([valor, texto]) => (
                  <label key={valor} className={`mc-opcion ${reglasEditables ? "" : "mc-opcion--inactiva"}`}>
                    <input type="radio" name="formato" checked={ajustes.formato === valor} disabled={!reglasEditables} onChange={() => controles.onAjustes({ formato: valor })} />
                    {texto}
                  </label>
                ))}
              </div>
              <div className="mc-opcion-fila">
                {([["TIEBREAK", "TieBreak"], ["SUPER_TIEBREAK", "Super TieBreak"]] as const).map(([valor, texto]) => (
                  <label key={valor} className={`mc-opcion ${reglasEditables ? "" : "mc-opcion--inactiva"}`}>
                    <input type="radio" name="desempate" checked={ajustes.desempate === valor} disabled={!reglasEditables} onChange={() => controles.onAjustes({ desempate: valor })} />
                    {texto}
                  </label>
                ))}
              </div>
              <div className="mc-opcion-fila">
                {([[true, "Punto de Oro"], [false, "Diferencia de 2"]] as const).map(([valor, texto]) => (
                  <label key={texto} className={`mc-opcion ${reglasEditables ? "" : "mc-opcion--inactiva"}`}>
                    <input type="radio" name="oro" checked={ajustes.puntoDeOro === valor} disabled={!reglasEditables} onChange={() => controles.onAjustes({ puntoDeOro: valor })} />
                    {texto}
                  </label>
                ))}
              </div>
              {!reglasEditables && <p className="mc-nota">Las reglas solo se pueden cambiar antes de iniciar la partida.</p>}
              <div className="mc-panel-separador" />
              <label className="mc-opcion mc-opcion--check">
                <input type="checkbox" checked={ajustes.descansoImpares} onChange={(e) => controles.onAjustes({ descansoImpares: e.target.checked })} />
                Descanso Impares/Cambio de Pista
              </label>
            </div>
          )}

          {esTablet && controles && panel === "audio" && ajustes && (
            <div className="mc-panel mc-panel-opciones mc-panel-audio">
              <label className="mc-opcion mc-opcion--check">
                <input type="checkbox" checked={ajustes.vozTanteo} onChange={(e) => controles.onAjustes({ vozTanteo: e.target.checked })} />
                Voz Tanteo (nombre winner/forzado)
              </label>
              <label className="mc-opcion mc-opcion--check">
                <input type="checkbox" checked={ajustes.vozPuntos} onChange={(e) => controles.onAjustes({ vozPuntos: e.target.checked })} />
                Voz Puntos (15-15, 30-15, etc)
              </label>
            </div>
          )}
        </div>

        <div className="mc-title">
          <div className="mc-title-linea">{pista}</div>
          <div className="mc-title-linea">{club}</div>
          <div className="mc-estado" style={{ color: COLORES_ESTADO[estado] }}>
            {textoEstado}
          </div>
        </div>

        <div className="mc-reloj">
          <div className="mc-reloj-hora">{`${dos(ahora.getHours())}:${dos(ahora.getMinutes())}:${dos(ahora.getSeconds())}`}</div>
          <div className="mc-reloj-fecha">{textoFecha(ahora)}</div>
          <div className="mc-reloj-clima">
            <span>
              <Thermometer fill="currentColor" strokeWidth={1.5} />
              {clima?.temperatura != null ? `${clima.temperatura}°C` : "--°C"}
            </span>
            <span>
              <Droplet fill="currentColor" strokeWidth={1.5} />
              {clima?.humedad != null ? `${clima.humedad}%` : "--%"}
            </span>
          </div>
        </div>
      </div>

      {/* ------------------------------------------------- cronómetro y cabeceras de columna */}
      <div className="mc-rejilla mc-columnas">
        <div className="mc-timer">{hhmmss(segundos)}</div>
        <div className="mc-columna-titulo">JUEGO</div>
        <div className="mc-columna-titulo">SET 1</div>
        <div className="mc-columna-titulo">SET 2</div>
        <div className="mc-columna-titulo">SET 3</div>
      </div>

      {filaEquipo("a")}
      <div />
      {filaEquipo("b")}
      <div />
      <Publicidad />

      {/* ------------------------------------------------------------------ avisos */}
      {enDescanso && (
        <div className="mc-cubierta">
          <div className="mc-cubierta-titulo">DESCANSO</div>
          <div className="mc-cubierta-cuenta">{`${dos(Math.floor(descanso! / 60))}:${dos(descanso! % 60)}`}</div>
          {esTablet && controles && (
            <button type="button" className="mc-cubierta-boton" onClick={controles.onSaltarDescanso}>
              Saltar descanso
            </button>
          )}
        </div>
      )}

      {ganador && m && (
        <div className="mc-cubierta" style={{ zIndex: 41 }}>
          <img className="mc-cubierta-trofeo" src="/scoreboard/trofeo.png" alt="" draggable={false} />
          <div className="mc-cubierta-titulo">GANA EL EQUIPO {ganador.toUpperCase()}</div>
          <div className="mc-cubierta-texto">{nombresGanadores.join(" / ")}</div>
          <div className="mc-cubierta-sets">
            {m.marcador.sets
              .filter((s) => s.estado !== "pendiente")
              .map((s, i) => (
                <span key={i}>
                  {s.a}-{s.b}
                </span>
              ))}
          </div>
        </div>
      )}

      {/* ------------------------------------------- cierre de paneles al pulsar fuera */}
      {panel && <div className="mc-scrim" onClick={() => setPanel(null)} />}

      {controles && (
        <>
          <ConfirmDialog
            open={confirmar === "reiniciar"}
            onOpenChange={(o) => !o && setConfirmar(null)}
            title="Reiniciar partida"
            description="El marcador y las estadísticas vuelven a cero y la partida a calentamiento. Se mantienen los jugadores y los ajustes."
            tone="danger"
            confirmLabel="Reiniciar"
            onConfirm={controles.onReiniciar}
          />
          <ConfirmDialog
            open={confirmar === "resetear"}
            onOpenChange={(o) => !o && setConfirmar(null)}
            title="Resetear partida"
            description="Se borra la partida completa: jugadores, ajustes, marcador y estadísticas. Tendrás que volver a indicar quiénes juegan."
            tone="danger"
            confirmLabel="Resetear"
            onConfirm={controles.onResetear}
          />
        </>
      )}
    </div>
  );
}
